import {
    State,
    MainThunkResult,
    MainThunkDispatch,
    MainAction
} from "../../../Reducers"
import {
    failMessage,
    successMessage,
    warnMessage
} from "../../Client/MessagesActions"
import { JobAPIType, TrackingType, InstanceAPIType } from "../../Types"
import {
    APIJobListParams,
    defaultParams,
    APIJobCreateResult,
    APIJobReviewResult,
    APIJobModel,
    JobStatus,
    JobSortBy,
    JobOrderBy,
    FeedbackMessageListParams,
    defaultJobMessagesParams,
    JobHistoryModel,
    APIJobCountByStatusParams,
    RelabelJobResponse,
    JobIdsOfBatchResponse,
    JobHistoryVersionResponseModel,
    JobHistoryQueryParams
} from "../../../Models/API/JobModel"
import {
    safePromise,
    execAndWaitForAll
} from "../../../Helpers/Functions/promise"
import { apiFetch, killPoll, pollAPI } from "../../Client/NetworkActions"
import { apiEndpoints } from "../../../Routes/api"
import { hasValue } from "../../../Models/Client/FetchState"
import { getValueById, hasMore } from "../../../Models/Client/RemoteData"
import { killOnboardingPolls } from "../AppTemplate/Interface/TemplateInstantiateActions"
import {
    getAsyncOperations,
    reloadTags
} from "../AppInstance/AppInstanceActions"
import { uploadData } from "../Data/DataActions"
import { randID } from "../../../Helpers/Functions/random"
import { JobSubmitData, getCount } from "../../../Models/Client/JobSubmitData"
import { BatchGetModel } from "../../../Models/API/BatchGetModel"
import { APIJobsPerStatusModel } from "../../../Models/API/APIJobsPerStatusModel"
import { JobCountStateKey } from "../../../Reducers/API/Job/JobReducer"
import { reportError } from "../../../Helpers/Functions/sentry"
import { embedReference } from "universal-schema/lib"
import { JSONSchema7 } from "json-schema"

const DEFAULT_PAGE_SIZE = 20
const RELABEL_JOB_ID_POLL = "relabel_job_id_poll"
const JOB_IDS_OF_BATCH_POLL = "job_ids_of_batch_poll"

const addTagsByJobIDImpl =
    (
        appID: string,
        jobID: string,
        tags: string[],
        updateItem: boolean = true
    ): MainThunkResult<Promise<void>> =>
    async (dispatch: MainThunkDispatch) => {
        const data = await dispatch(
            apiFetch<APIJobModel>(
                String(Math.random()),
                apiEndpoints.tagsByJobID({ jobID }),
                {
                    method: "PUT",
                    body: JSON.stringify({
                        tags
                    })
                }
            )
        )

        if (updateItem) {
            dispatch({
                type: JobAPIType.setOne,
                meta: {
                    appID,
                    jobID
                },
                payload: data
            })
            dispatch(setOneJobWithMessage(jobID, appID, data))
        }
    }

const removeTagsByJobIDImpl =
    (
        appID: string,
        jobID: string,
        tags: string[],
        updateItem: boolean = true
    ): MainThunkResult<Promise<void>> =>
    async (dispatch: MainThunkDispatch) => {
        const data = await dispatch(
            apiFetch<APIJobModel>(
                String(Math.random()),
                apiEndpoints.tagsByJobID({ jobID }),
                {
                    method: "DELETE",
                    body: JSON.stringify({
                        tags
                    })
                }
            )
        )

        if (updateItem) {
            dispatch({
                type: JobAPIType.setOne,
                meta: {
                    appID,
                    jobID
                },
                payload: data
            })

            dispatch(setOneJobWithMessage(jobID, appID, data))
        }
    }

const startMonitorBatchProgress =
    (appID: string, batchID: string, totalJobCountBeforeSubmit: number) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const batch = await dispatch(
            apiFetch<BatchGetModel>(
                `GET_BATCH_${batchID}`,
                apiEndpoints.getProcessingBatch({ appUUID: appID, batchID })
            )
        )

        if (batch === undefined) {
            return
        }

        const expectedCount = batch.batch_total + totalJobCountBeforeSubmit

        dispatch({
            type: InstanceAPIType.startBatchSubmit,
            meta: { appID },
            payload: batch.batch_total
        })

        while (true) {
            const params = getState().api.jobs.table[appID]?.list?.params

            if (params === undefined) {
                await new Promise(r => setTimeout(r, 3000))
                continue
            }

            const jobListArgs = {
                appID,
                page: 0,
                pageSize: DEFAULT_PAGE_SIZE,
                ...params
            }
            const endpoint = apiEndpoints.getJobCountByStatus({ appID })
            const [jobCounts, jobs] = await Promise.all([
                dispatch(
                    apiFetch<APIJobsPerStatusModel>(endpoint.url, endpoint)
                ),
                dispatch(
                    apiFetch<{ jobs: APIJobModel[]; total: number }>(
                        `GET_JOB_LIST_${appID}_${JSON.stringify(jobListArgs)}`,
                        apiEndpoints.getJobList(jobListArgs)
                    )
                )
            ])

            if (jobCounts === undefined || jobs === undefined) {
                await new Promise(r => setTimeout(r, 3000))
                continue
            }

            const total = APIJobsPerStatusModel.total(jobCounts)
            // expectedCount can be smaller than app.job_stats.total if new jobs are enqueued while waiting for previous batch
            const remainingCount = Math.max(0, expectedCount - total)

            dispatch({
                type: InstanceAPIType.progressBatchSubmit,
                meta: {
                    appID,
                    params,
                    page: 0,
                    total,
                    filteredTotal: jobs.total
                },
                payload: { jobs: jobs.jobs, remainingCount }
            })

            if (remainingCount === 0) {
                dispatch(reloadTags(appID))
                return
            }

            await new Promise(r => setTimeout(r, 3000))
        }
    }

export const relabelJob =
    (
        jobID: string,
        squashSuccessMessage: boolean
    ): MainThunkResult<Promise<RelabelJobResponse | undefined>> =>
    async (dispatch: MainThunkDispatch) => {
        const { data, error } = await safePromise<RelabelJobResponse>(
            dispatch(
                apiFetch(
                    `RELABEL_JOB_${jobID}`,
                    apiEndpoints.relabelJob({ jobID }),
                    { method: "POST" }
                )
            )
        )
        if (error !== undefined) {
            dispatch(failMessage("Could not reprocess data point", error))
            throw error
        }

        if (!squashSuccessMessage) {
            dispatch(
                successMessage(
                    `A copy of the data point has been created and sent for processing`
                )
            )
        }

        return data
    }

export const requestSelectedJobsRelabel =
    (appID: string, jobIDList: string[]): MainThunkResult<Promise<any>> =>
    async (dispatch: MainThunkDispatch) => {
        const totalToRelabel = jobIDList ? jobIDList.length || 0 : 0

        if (totalToRelabel === 0) {
            return
        }

        await execAndWaitForAll(
            jobIDList.map(id => dispatch(relabelJob(id, true)))
        )

        dispatch(
            successMessage(
                `Successfully submitted data points for reprocessing`
            )
        )

        dispatch(refreshJobsList(appID))
        dispatch(invalidateJobCounts(appID))
    }

export const requestFilteredJobsRelabel =
    (appID: string, params: APIJobListParams): MainThunkResult<Promise<any>> =>
    async (dispatch: MainThunkDispatch) => {
        const request = { appID, ...params }
        const { error, data } = await safePromise(
            dispatch(
                apiFetch<unknown>(
                    `RELABEL_JOB_${JSON.stringify(request)}`,
                    apiEndpoints.relabelJobs(request),
                    {
                        method: "POST"
                    }
                )
            )
        )

        if (error !== undefined) {
            dispatch(failMessage(`Could not reprocess data points`, error))
            throw error
        }

        if (data === undefined) {
            return
        }

        dispatch(
            successMessage(
                `Successfully submitted data points for reprocessing`
            )
        )

        dispatch(refreshJobsList(appID, params))
        dispatch(invalidateJobCounts(appID))
    }

export const cancelJob =
    (
        appID: string,
        jobID: string,
        squashSuccessMessage: boolean,
        updateItem: boolean = true
    ): MainThunkResult<Promise<any>> =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const { error, data } = await safePromise<APIJobModel>(
            dispatch(
                apiFetch(
                    `CANCEL_JOB_${jobID}`,
                    apiEndpoints.cancelJob({ jobID }),
                    {
                        method: "POST"
                    }
                )
            )
        )

        if (error !== undefined) {
            dispatch(failMessage("Could not cancel data point", error))
            throw error
        }

        if (!squashSuccessMessage) {
            dispatch(
                successMessage(
                    `Successfully cancelled processing on data point: ${jobID}`
                )
            )
        }

        if (data !== undefined && updateItem) {
            dispatch({
                type: JobAPIType.setOne,
                meta: { appID, jobID },
                payload: data
            })
            dispatch(setOneJobWithMessage(jobID, appID, data))
        }

        /**
         * If we are polling for this job, we should cancel the polling
         */
        const onboarding = getState().api.interface.templateInstantiate
        if (data && onboarding && onboarding.jobAttempts.includes(data.uuid)) {
            dispatch(killOnboardingPolls())
        }
    }

export const requestSelectedJobsCancel =
    (appID: string, jobIDList: string[]): MainThunkResult<Promise<any>> =>
    async (dispatch: MainThunkDispatch) => {
        const totalToCancel = jobIDList ? jobIDList.length || 0 : 0

        if (totalToCancel === 0) {
            return
        }

        await execAndWaitForAll(
            jobIDList.map(id => dispatch(cancelJob(appID, id, true, false)))
        )

        dispatch(successMessage(`Successfully cancelled data points`))

        dispatch(refreshJobsList(appID))
        dispatch(invalidateJobCounts(appID))
    }

export const requestFilteredJobsCancel =
    (appID: string, params: APIJobListParams): MainThunkResult<Promise<any>> =>
    async (dispatch: MainThunkDispatch) => {
        const request = { appID, ...params }
        const { error, data } = await safePromise(
            dispatch(
                apiFetch<unknown>(
                    `CANCEL_JOB_${JSON.stringify(request)}`,
                    apiEndpoints.cancelJobs(request),
                    {
                        method: "POST"
                    }
                )
            )
        )

        if (error !== undefined) {
            dispatch(failMessage("Could not cancel data points", error))
            throw error
        }

        if (data === undefined) {
            return
        }

        dispatch(successMessage(`Successfully cancelled data points`))

        dispatch(refreshJobsList(appID, params))
        dispatch(invalidateJobCounts(appID))
    }

interface CreateJobsAdvancedOptions {
    queue: boolean
    tags: string[]
    strict: boolean
}

export const createJobs =
    (
        appID: string,
        jobSubmitData: JobSubmitData,
        totalJobCountBeforeSubmit: number,
        { queue, tags, strict }: CreateJobsAdvancedOptions
    ) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const acceptMeasurementJobs =
            getState().client.browser.calibrationJobAcceptedInstanceIds.includes(
                appID
            )
        const bodyCommon = {
            accept_measurement_jobs: acceptMeasurementJobs,
            pending: !queue,
            tags,
            no_strict: !strict
        }
        const body =
            jobSubmitData.type === "raw"
                ? { ...bodyCommon, inputs: jobSubmitData.jobs }
                : await fetch(jobSubmitData.objectURL)
                      .then(res => res.json())
                      .then(object => {
                          const options = { type: "application/json" }
                          const blob = new Blob(
                              [JSON.stringify(object)],
                              options
                          )
                          const path = `tmp/submit_data_${Date.now()}.json`

                          return dispatch(uploadData(blob, path))
                      })
                      .then(inputsDataUrl => ({ ...bodyCommon, inputsDataUrl }))
                      .catch(e => {
                          dispatch(failMessage("Could not upload data", e))
                          throw e
                      })
        const count = getCount(jobSubmitData)
        const { error, data } = await safePromise(
            dispatch(
                apiFetch<APIJobCreateResult>(
                    `CREATE_JOB`,
                    apiEndpoints.createJob({ id: appID }),
                    {
                        method: "POST",
                        body: JSON.stringify(body)
                    }
                )
            )
        )

        const word = `${count} data point${count > 1 ? "s" : ""}`

        if (error !== undefined) {
            dispatch(failMessage(`Could not create ${word}`, error))
            throw error
        }

        if (data === undefined) return

        if (data.truncated_inputs) {
            dispatch(
                warnMessage(
                    `Part of data points are successfully submitted. ${data.message}`
                )
            )
        } else {
            dispatch(
                successMessage(
                    `${word} submitted. They may take a few seconds to appear`
                )
            )
        }

        // revoke object URL after successful job submission to free memory
        if (jobSubmitData.type === "upload") {
            URL.revokeObjectURL(jobSubmitData.objectURL)
        }

        dispatch(
            startMonitorBatchProgress(
                appID,
                data.batch_id,
                totalJobCountBeforeSubmit
            )
        )
    }

export const createOnboardingJob =
    (appID: string, code: number, data: JobSubmitData) =>
    async (dispatch: MainThunkDispatch) => {
        const { error } = await safePromise(
            dispatch(
                apiFetch(
                    `CREATE_ONBOARDING_JOB`,
                    apiEndpoints.createOnboardingJob({ appID, code }),
                    {
                        method: "POST",
                        body: JSON.stringify(
                            data.type === "raw"
                                ? { inputs: data.jobs, outputs: data.outputs }
                                : { inputsDataUrl: data.objectURL }
                        )
                    }
                )
            )
        )

        if (error !== undefined) {
            dispatch(failMessage("Could not create data point", error))
            throw error
        }
    }

export const loadMoreJobList =
    (appID: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const table = getState().api.jobs.table[appID]

        if (table !== undefined && table.list !== undefined && hasMore(table)) {
            await dispatch(
                getJobList(appID, table.list.params, table.list.pages.length)
            )
        }
    }

export const getFilteredJobCountByStatus =
    (
        appID: string,
        stateKey: JobCountStateKey,
        params: APIJobCountByStatusParams
    ) =>
    async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getJobCountByStatus({ appID, ...params })
        const { data } = await safePromise(
            dispatch(
                apiFetch<APIJobsPerStatusModel>(
                    `${endpoint.url}${stateKey}`,
                    endpoint
                )
            )
        )

        if (data !== undefined) {
            dispatch({
                type: JobAPIType.setJobCount,
                meta: {
                    appID,
                    stateKey,
                    params
                },
                payload: data
            })
        }
    }

export const getJobList =
    (
        appID: string,
        params: APIJobListParams,
        page: number = 0,
        pageSize: number = DEFAULT_PAGE_SIZE
    ) =>
    async (dispatch: MainThunkDispatch) => {
        const { data, error } = await safePromise(
            dispatch(
                apiFetch<{ jobs: APIJobModel[]; total: number }>(
                    `GET_JOB_LIST_${appID}_${JSON.stringify({
                        page,
                        pageSize,
                        ...params
                    })}`,
                    apiEndpoints.getJobList({
                        appID,
                        page,
                        pageSize,
                        ...params
                    })
                )
            )
        )

        if (error !== undefined || data === undefined || !data.jobs) {
            return
        }

        dispatch({
            type: JobAPIType.setList,
            meta: {
                appID,
                page,
                total: data.total,
                params
            },
            payload: data.jobs
        })
    }

export const reloadJobList =
    (appID: string) => (dispatch: MainThunkDispatch, getState: () => State) => {
        const params = getState().api.jobs.table[appID]?.list?.params

        if (params === undefined) {
            return
        }

        dispatch(getJobList(appID, params))
    }

export const getJob =
    (appID: string, jobID: string) => async (dispatch: MainThunkDispatch) => {
        const { data, error } = await safePromise<APIJobModel>(
            dispatch(
                apiFetch(`GET_JOB_${jobID}`, apiEndpoints.getJobByID({ jobID }))
            )
        )

        if (error !== undefined || data === undefined) {
            return
        }

        dispatch({
            type: JobAPIType.setOne,
            meta: {
                appID,
                jobID
            },
            payload: data
        })

        return data
    }

export const getJobHistory =
    (jobID: string, page: number = 0, pageSize: number = 1000) =>
    async (dispatch: MainThunkDispatch) => {
        const { data, error } = await safePromise<JobHistoryModel>(
            dispatch(
                apiFetch(
                    `GET_JOB_HISTORY_${jobID}`,
                    apiEndpoints.getJobHistory({
                        jobID,
                        page,
                        pageSize,
                        ...JobHistoryQueryParams
                    })
                )
            )
        )

        if (error !== undefined || data === undefined) {
            return
        }

        dispatch({
            type: JobAPIType.setJobHistory,
            meta: {
                jobID
            },
            payload: data
        })

        return data
    }

export const getJobHistoryVersionResponse =
    (appID: string, jobID: string, jobVersion: number) =>
    async (dispatch: MainThunkDispatch) => {
        const { data, error } =
            await safePromise<JobHistoryVersionResponseModel>(
                dispatch(
                    apiFetch(
                        `GET_JOB_HISTORY_VERSION_RESPONSE_${jobID}_${jobVersion}`,
                        apiEndpoints.getJobHistoryVersionResponse({
                            jobID,
                            jobVersion
                        })
                    )
                )
            )

        if (data === undefined) {
            return
        }

        if (error !== undefined) {
            reportError(error)
            return
        }

        dispatch({
            type: JobAPIType.setJobHistoryVersionResponse,
            meta: {
                appID,
                jobID,
                jobVersion: jobVersion
            },
            payload: data
        })
    }

export const getJobResponse =
    (jobID: string) => async (dispatch: MainThunkDispatch) => {
        const { data, error } = await safePromise<APIJobModel>(
            dispatch(
                apiFetch(`GET_JOB_BY_${jobID}`, apiEndpoints.getJobByID({ jobID }))
            )
        )
        if (error !== undefined) {
            dispatch({
                type: JobAPIType.setResponseError,
                meta: {
                    jobID
                },
                payload: error.message
            })
        }
        const isBigResponse = data?.isBigResponse
        if (isBigResponse) {
            let { data, error } = await safePromise(
                dispatch(
                    apiFetch(
                        `GET_JOB_BIG_PAYLOAD_${jobID}`,
                        apiEndpoints.getJobBigPayloadByID({ jobID }),
                        undefined,
                        3000
                    )
                )
            )
            if (error !== undefined || data === undefined) {
                if (error !== undefined) {
                    dispatch({
                        type: JobAPIType.setResponseError,
                        meta: {
                            jobID
                        },
                        payload: error.message
                    })
                }

                return
            }
            try {
                const response = await fetch(data.url)
                if (!response.ok) {
                    throw new Error("Failed to load job output.")
                }
                const { body } = await response.json()
                const payload = {
                    response: body?.annotation,
                    responseData: undefined
                }
                dispatch({
                    type: JobAPIType.setResponse,
                    meta: {
                        jobID
                    },
                    payload
                })
            } catch (error) {
                dispatch({
                    type: JobAPIType.setResponseError,
                    meta: { jobID },
                    payload: (error as Error).message
                })

                return
            }
        } else {
            const { data, error } = await safePromise(
                dispatch(
                    apiFetch(
                        `GET_JOB_RESPONSE_${jobID}`,
                        apiEndpoints.getJobResponseByID({ jobID }),
                        undefined,
                        3000
                    )
                )
            )
            if (error !== undefined || data === undefined) {
                if (error !== undefined) {
                    dispatch({
                        type: JobAPIType.setResponseError,
                        meta: {
                            jobID
                        },
                        payload: error.message
                    })
                }

                return
            }
            dispatch({
                type: JobAPIType.setResponse,
                meta: {
                    jobID
                },
                payload: data
            })
        }
    }

export const startPollJobResponse =
    (appID: string, jobID: string) => async (dispatch: MainThunkDispatch) => {
        dispatch(
            pollAPI(
                `POLL_JOB_RESPONSE_${jobID}`,
                apiEndpoints.getJobResponseByID({ jobID }),
                response => {
                    if (response !== undefined) {
                        dispatch({
                            type: JobAPIType.setResponse,
                            meta: {
                                jobID
                            },
                            payload: response
                        })
                        dispatch(getJob(appID, jobID))
                    }
                },
                async error => {
                    if (error !== undefined) {
                        const errorText = await error.text()
                        return dispatch({
                            type: JobAPIType.setResponseError,
                            meta: {
                                jobID
                            },
                            payload: errorText
                        })
                    }
                },
                5000,
                true
            )
        )
    }

export const stopPollJobResponse =
    (jobID: string) => async (dispatch: MainThunkDispatch) => {
        dispatch(killPoll(`POLL_JOB_RESPONSE_${jobID}`))
    }

export const updateJobOutput =
    (
        appID: string,
        jobUUID: string,
        jobID: string,
        output: unknown,
        outputSchema: JSONSchema7,
        uploadPathPrefix: string,
        squashMessage?: boolean,
        createsHistory = true,
        postProcess = true
    ) =>
    async (dispatch: MainThunkDispatch): Promise<void> => {
        const convertedOutput = await embedReference(
            output,
            outputSchema,
            async (blob: Blob) => {
                const path = `${uploadPathPrefix}/${randID()}.json`
                return dispatch(uploadData(blob, path))
            }
        )

        // For uploading data for a job, jobUUID is required instead of jobID
        const response = await dispatch(
            apiFetch<Response>(
                `UPDATE_JOB_${jobID}`,
                apiEndpoints.reviewJob({
                    jobID: jobUUID,
                    createsHistory,
                    postProcess
                }),
                {
                    method: "PATCH",
                    body: JSON.stringify({
                        response: convertedOutput
                    })
                },
                200,
                true
            )
        )
        if (response === undefined) {
            throw Error("")
        }

        const { error, data } = await safePromise<APIJobReviewResult>(
            response.json()
        )
        if (error !== undefined || data === undefined) {
            if (error !== undefined && !squashMessage) {
                dispatch(failMessage("Could not update data point", error))
            }

            throw Error((error && error.message) || "")
        }
        if (data.operationId !== undefined) {
            await dispatch(getAsyncOperations(appID))
        }

        dispatch(setJobReviewResult(appID, jobID, data))
        dispatch({ type: JobAPIType.setOne, meta: { appID, jobID } })

        if (!squashMessage) {
            dispatch(successMessage("Data point updated."))
        }

        await dispatch(getJobHistory(jobID))
    }

export const markJobCorrectness =
    (
        appID: string,
        jobID: string,
        correct: boolean
    ): MainThunkResult<Promise<void>> =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `UPDATE_JOB_${jobID}`,
                    apiEndpoints.reviewJob({ jobID }),
                    {
                        method: "PATCH",
                        body: JSON.stringify({
                            correct
                        })
                    }
                )
            )
        )

        if (data === undefined || error !== undefined) {
            if (error !== undefined) {
                dispatch(failMessage("Could not update data point", error))
            }

            return
        }

        dispatch(setJobReviewResult(appID, jobID, data))

        dispatch(getJobToReview(appID))

        const entry = getState().api.jobs.table[appID]

        const job = entry === undefined ? undefined : entry.records.get(jobID)

        if (hasValue(job)) {
            dispatch({
                type: TrackingType.job_setCorrectness,
                payload: job.value
            })
        }
    }

export const getJobToLabel =
    (appID: string): MainThunkResult<Promise<void>> =>
    async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getJobList({
            appID,
            page: 0,
            pageSize: 1,
            statusIn: [JobStatus.IN_PROGRESS],
            sortBy: JobSortBy.id,
            orderBy: JobOrderBy.asc
        })

        const { data, error } = await safePromise(
            dispatch(apiFetch(`job_to_review_${appID}`, endpoint))
        )

        if (error !== undefined || data === undefined) {
            return
        }

        dispatch({
            type: JobAPIType.setJobIdToLabel,
            meta: {
                appID
            },
            payload: data.jobs.length > 0 ? data.jobs[0].id : undefined
        })
    }

export const getJobToReview =
    (appID: string): MainThunkResult<Promise<void>> =>
    async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getJobList({
            appID,
            page: 0,
            pageSize: 1,
            reviewed: ["NOT_REVIEWED"],
            statusIn: [JobStatus.COMPLETED],
            sortBy: JobSortBy.completed,
            orderBy: JobOrderBy.asc
        })

        const { data, error } = await safePromise(
            dispatch(apiFetch(`job_to_label_${appID}`, endpoint))
        )

        if (error !== undefined || data === undefined) {
            return
        }

        dispatch({
            type: JobAPIType.setJobIdToReview,
            meta: {
                appID
            },
            payload:
                data.jobs.length > 0
                    ? { jobId: data.jobs[0].id, total: data.total }
                    : "nothing_to_review"
        })
    }

const setJobReviewResult =
    (
        appID: string,
        jobID: string,
        { correct, response, baselineId, responseData }: APIJobReviewResult
    ) =>
    (dispatch: MainThunkDispatch, getState: () => State): void => {
        const job = getValueById(getState().api.jobs.table[appID], jobID)
        const jobOutput = getState().api.jobs.outputs[jobID]

        if (job !== undefined) {
            dispatch({
                type: JobAPIType.setOne,
                meta: {
                    appID,
                    jobID
                },
                payload: {
                    ...job,
                    baselineId: baselineId === null ? null : String(baselineId),
                    correct
                }
            })
        }

        const jobWithMessage = getValueById(
            getState().api.jobs.messageTable[appID],
            jobID
        )
        if (jobWithMessage !== undefined) {
            dispatch({
                type: JobAPIType.setOneJobWithMessage,
                meta: {
                    appID,
                    jobID
                },
                payload: {
                    ...jobWithMessage,
                    baselineId: baselineId === null ? null : String(baselineId),
                    correct
                }
            })
        }

        if (hasValue(jobOutput)) {
            dispatch({
                type: JobAPIType.setResponse,
                meta: {
                    jobID
                },
                payload: {
                    ...jobOutput.value,
                    response,
                    responseData
                }
            })
        }
    }

export const enqueueBatch =
    (appID: string, params: APIJobListParams) =>
    async (dispatch: MainThunkDispatch) => {
        const request = { appID, ...params }
        const { error, data } = await safePromise(
            dispatch(
                apiFetch<{ started: number }>(
                    `ENQUEUE_BATCH_${JSON.stringify(request)}`,
                    apiEndpoints.enqueueBatch(request),
                    {
                        method: "POST"
                    }
                )
            )
        )

        if (error !== undefined) {
            dispatch(failMessage("Could not queue data points", error))
            throw error
        }

        if (data === undefined) {
            return
        }

        dispatch(
            successMessage(`Successfully queued ${data.started} data points`)
        )

        dispatch(refreshJobsList(appID, params))
    }

export const enqueueSelected =
    (appID: string, jobIDList: string[]): MainThunkResult<Promise<any>> =>
    async (dispatch: MainThunkDispatch) => {
        const totalToEnqueue = jobIDList ? jobIDList.length || 0 : 0

        if (totalToEnqueue === 0) {
            return
        }

        await execAndWaitForAll(
            jobIDList.map(id => dispatch(enqueueJob(appID, id, true)))
        )

        dispatch(successMessage(`Successfully queued data points`))

        dispatch(invalidateJobCounts(appID))
    }

export const enqueueJob =
    (
        appID: string,
        id: string,
        squashSuccessMessage: boolean
    ): MainThunkResult<Promise<any>> =>
    async (dispatch: MainThunkDispatch) => {
        const { error } = await safePromise(
            dispatch(
                apiFetch(
                    `ENQUEUE_JOB_${id}`,
                    apiEndpoints.enqueue({ id }),
                    {
                        method: "POST"
                    },
                    undefined,
                    true
                )
            )
        )

        dispatch(getJob(appID, id))

        if (error !== undefined) {
            dispatch(failMessage("Could not queue data point", error))
            throw error
        }

        if (!squashSuccessMessage) {
            dispatch(successMessage(`Data point has been queued`))
        }

        dispatch(invalidateJobCounts(appID))
    }

export const deleteBatch =
    (appID: string, params: APIJobListParams) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const request = {
            appID,
            ...params
        }

        const { error, data } = await safePromise(
            dispatch(
                apiFetch<{ deleted: number }>(
                    `DELETE_BATCH_${JSON.stringify(request)}`,
                    apiEndpoints.deleteBatch(request),
                    {
                        method: "DELETE"
                    }
                )
            )
        )

        if (data === undefined) {
            return
        }

        if (error !== undefined) {
            dispatch(failMessage("Could not delete data points", error))
            throw error
        }

        dispatch(successMessage(`Successfully deleted data points`))

        dispatch(refreshJobsList(appID, params))

        dispatch(reloadTags(appID))
    }

export const deleteSelected =
    (appID: string, jobIDList: string[]): MainThunkResult<Promise<any>> =>
    async (dispatch: MainThunkDispatch) => {
        const totalToDelete = jobIDList ? jobIDList.length || 0 : 0

        if (totalToDelete === 0) {
            return
        }

        await execAndWaitForAll(
            jobIDList.map(id =>
                dispatch(deleteJob(appID, id, true, false, false))
            )
        )

        dispatch(successMessage(`Successfully deleted data points`))

        dispatch(reloadJobList(appID))
        dispatch(reloadJobFeedbackMessageList(appID))
        dispatch(invalidateJobCounts(appID))
    }

export const deleteJob =
    (
        appID: string,
        id: string,
        squashSuccessMessage: boolean,
        updateStats: boolean = true,
        updateItem: boolean = true
    ): MainThunkResult<Promise<any>> =>
    async (dispatch: MainThunkDispatch) => {
        const { error } = await safePromise(
            dispatch(
                apiFetch(
                    `DELETE_JOB_${id}`,
                    apiEndpoints.deleteJob({ id }),
                    {
                        method: "DELETE"
                    },
                    undefined,
                    true
                )
            )
        )

        if (error !== undefined) {
            dispatch(failMessage("Could not delete data point", error))
            throw error
        }

        if (!squashSuccessMessage) {
            dispatch(successMessage(`The data point has been deleted`))
        }

        if (updateItem) {
            dispatch({
                type: JobAPIType.setOne,
                meta: {
                    appID,
                    jobID: id
                }
            })
            dispatch(setOneJobWithMessage(id, appID))
        }

        if (updateStats) dispatch(invalidateJobCounts(appID))
    }

export const addTagByJobID =
    (
        appID: string,
        jobID: string,
        tag: string
    ): MainThunkResult<Promise<void>> =>
    async (dispatch: MainThunkDispatch) => {
        const { error } = await safePromise(
            dispatch(addTagsByJobIDImpl(appID, jobID, [tag]))
        )

        if (error !== undefined) {
            dispatch(failMessage("Could not update tags.", error))
            throw error
        }

        dispatch(reloadTags(appID))
    }

export const removeTagByJobID =
    (
        appID: string,
        jobID: string,
        tag: string
    ): MainThunkResult<Promise<void>> =>
    async (dispatch: MainThunkDispatch) => {
        const { error } = await safePromise(
            dispatch(removeTagsByJobIDImpl(appID, jobID, [tag]))
        )

        if (error !== undefined) {
            dispatch(failMessage("Could not delete tags", error))
            throw error
        }
    }

export const batchAddTagsByJobID =
    (
        appID: string,
        jobIDs: string[],
        tags: string[]
    ): MainThunkResult<Promise<void>> =>
    async (dispatch: MainThunkDispatch) => {
        try {
            await execAndWaitForAll(
                jobIDs.map(jobID =>
                    dispatch(addTagsByJobIDImpl(appID, jobID, tags, false))
                )
            )
            dispatch(successMessage("Tags for these jobs are updated"))
            dispatch(reloadTags(appID))
            dispatch(reloadJobList(appID))
            dispatch(reloadJobFeedbackMessageList(appID))
        } catch (e) {
            dispatch(failMessage("Could not update all tags", e))
            throw e
        }
    }

export const batchRemoveTagsByJobID =
    (
        appID: string,
        jobIDs: string[],
        tags: string[]
    ): MainThunkResult<Promise<void>> =>
    async (dispatch: MainThunkDispatch) => {
        try {
            await execAndWaitForAll(
                jobIDs.map(jobID =>
                    dispatch(removeTagsByJobIDImpl(appID, jobID, tags, false))
                )
            )
            dispatch(successMessage("Tags for these jobs are updated"))
            dispatch(reloadJobList(appID))
            dispatch(reloadJobFeedbackMessageList(appID))
        } catch (e) {
            dispatch(failMessage("Could not update all tags", e))
            throw e
        }
    }

export const batchAddTagsByFilter =
    (
        appID: string,
        params: APIJobListParams,
        tags: string[]
    ): MainThunkResult<Promise<void>> =>
    async (dispatch: MainThunkDispatch) => {
        const { error } = await safePromise(
            dispatch(
                apiFetch<APIJobModel>(
                    `ADD_TAGS_${appID}_${JSON.stringify(params)}`,
                    apiEndpoints.tags({ id: appID, ...params }),
                    {
                        method: "PUT",
                        body: JSON.stringify({
                            tags
                        })
                    },
                    undefined,
                    true
                )
            )
        )

        if (error !== undefined) {
            dispatch(failMessage("Could not update tags", error))
            throw error
        }

        dispatch(successMessage("Tags for these jobs are updated"))
        dispatch(reloadTags(appID))
        dispatch(reloadJobList(appID))
        dispatch(reloadJobFeedbackMessageList(appID))
    }

export const batchRemoveTagsByFilter =
    (
        appID: string,
        params: APIJobListParams,
        tags: string[]
    ): MainThunkResult<Promise<void>> =>
    async (dispatch: MainThunkDispatch) => {
        const { error } = await safePromise(
            dispatch(
                apiFetch<APIJobModel>(
                    `REMOVE_TAGS_${appID}_${JSON.stringify(params)}`,
                    apiEndpoints.tags({ id: appID, ...params }),
                    {
                        method: "DELETE",
                        body: JSON.stringify({
                            tags
                        })
                    },
                    undefined,
                    true
                )
            )
        )

        if (error !== undefined) {
            dispatch(failMessage("Could not delete tags", error))
            throw error
        }

        dispatch(successMessage("Tags for these jobs are updated"))
        dispatch(reloadJobList(appID))
        dispatch(reloadJobFeedbackMessageList(appID))
    }

export const getFeedbackMessages =
    (
        jobID: string,
        params: FeedbackMessageListParams,
        page: number = 0,
        pageSize = DEFAULT_PAGE_SIZE,
        ownerID?: string
    ) =>
    async (dispatch: MainThunkDispatch) => {
        const allParams = { ...params, page, pageSize }
        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `GET_FEEDBACK_MESSAGES_${jobID}_${JSON.stringify(
                        allParams
                    )}`,
                    apiEndpoints.getFeedbackMessages({
                        jobID,
                        ownerID,
                        ...allParams
                    })
                )
            )
        )

        if (error !== undefined || data === undefined) {
            return
        }

        dispatch({
            type: JobAPIType.setJobConversation,
            meta: {
                jobID: jobID,
                total: data.total,
                page,
                params
            },
            payload: data
        })
    }

export const loadMoreFeedbackMessages =
    (jobID: string, ownerID?: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const conversations = getState().api.jobs.jobIdToConversation[jobID]

        if (
            conversations !== undefined &&
            conversations.list !== undefined &&
            hasMore(conversations)
        ) {
            await dispatch(
                getFeedbackMessages(
                    jobID,
                    conversations.list.params,
                    conversations.list.pages.length,
                    DEFAULT_PAGE_SIZE,
                    ownerID
                )
            )
        }
    }

export const createFeedbackMessage =
    (
        jobId: string,
        message: string,
        taskId?: string,
        isLabelByCollaborator?: boolean,
        correct?: boolean,
        onSuccessMessage = "successfully sent message",
        onErrorMessage = "Could not send message"
    ) =>
    async (dispatch: MainThunkDispatch) => {
        const options: RequestInit = {
            method: "POST",
            body: JSON.stringify({
                message,
                correct,
                taskId: isLabelByCollaborator ? undefined : taskId,
                collabTaskId: isLabelByCollaborator ? taskId : undefined
            })
        }
        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `CREATE_FEEDBACK_MESSAGE_${jobId}`,
                    apiEndpoints.createFeedbackMessage({ jobID: jobId }),
                    options
                )
            )
        )

        if (error !== undefined || data === undefined) {
            if (error !== undefined) {
                dispatch(failMessage(onErrorMessage))
            }

            return
        }

        if (!onSuccessMessage) {
            dispatch(successMessage(onSuccessMessage))
        }

        dispatch({
            type: JobAPIType.addJobMessage,
            meta: {
                jobID: jobId
            }
        })

        return data
    }

export const getFeedbackTags =
    (appID: string) => async (dispatch: MainThunkDispatch) => {
        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `GET_FEEDBACK_TAGS_${appID}`,
                    apiEndpoints.getFeedbackTags({ appID })
                )
            )
        )
        if (error !== undefined || data === undefined) {
            return
        }
        dispatch({
            type: JobAPIType.setFeedbackTags,
            meta: {
                appID: appID
            },
            payload: data
        })
    }

export const getJobFeedbackMessageList =
    (
        appID: string,
        params: APIJobListParams,
        page: number = 0,
        pageSize: number = DEFAULT_PAGE_SIZE
    ) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const { timeOfEntering } = getState().api.jobs.detailPage
        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `GET_JOB_FEEDBACK_LIST_${appID}_${JSON.stringify({
                        page,
                        pageSize,
                        ...params
                    })}`,
                    apiEndpoints.getJobFeedbackMessageList({
                        appID,
                        page,
                        pageSize,
                        ...params,
                        feedbackMessageCreatedBefore:
                            params.sortBy === "fmcreated"
                                ? timeOfEntering?.toISOString()
                                : undefined
                    })
                )
            )
        )

        if (error !== undefined || data === undefined || !data.jobs) {
            return
        }

        dispatch({
            type: JobAPIType.setMessageList,
            meta: {
                appID,
                page,
                total: data.total,
                params
            },
            payload: data.jobs
        })
    }

export const loadMoreJobFeedbackMessageList =
    (appID: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const table = getState().api.jobs.messageTable[appID]

        if (table !== undefined && table.list !== undefined && hasMore(table)) {
            await dispatch(
                getJobFeedbackMessageList(
                    appID,
                    table.list.params,
                    table.list.pages.length
                )
            )
        }
    }

export const updateFeedbackMessageViewState =
    (jobID: string, appID: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const options: RequestInit = {
            method: "POST"
        }
        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `UPDATE_FEEDBACK_MESSAGE_VIEW_STATE_${jobID}`,
                    apiEndpoints.getFeedbackMessageViewState({ jobID }),
                    options
                )
            )
        )

        if (error !== undefined || data === false) {
            return false
        }
        dispatch(getJobWithMessage(appID, jobID))
        return data
    }

const refreshJobsList =
    (appID: string, params?: APIJobListParams) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const table = getState().api.jobs.table[appID]
        const jobParams = params
            ? params
            : table === undefined || table.list === undefined
            ? defaultParams
            : table.list.params
        dispatch(getJobList(appID, jobParams)).catch(() => null)

        const messagesTable = getState().api.jobs.messageTable[appID]
        const jobMessagesParams =
            messagesTable === undefined || messagesTable.list === undefined
                ? defaultJobMessagesParams
                : messagesTable.list.params
        dispatch(getJobFeedbackMessageList(appID, jobMessagesParams)).catch(
            () => null
        )
    }

export const reloadJobFeedbackMessageList =
    (appID: string) => (dispatch: MainThunkDispatch, getState: () => State) => {
        const params = getState().api.jobs.messageTable[appID]?.list?.params

        if (params === undefined) {
            return
        }

        dispatch(getJobFeedbackMessageList(appID, params))
    }

export const getJobWithMessage =
    (appID: string, jobID: string) => async (dispatch: MainThunkDispatch) => {
        const { data, error } = await safePromise<APIJobModel>(
            dispatch(
                apiFetch(
                    `GET_JOB_WITH_MESSAGE_${jobID}`,
                    apiEndpoints.getJobWithMessageByID({ jobID })
                )
            )
        )

        if (error !== undefined || data === undefined) {
            return
        }

        dispatch({
            type: JobAPIType.setOneJobWithMessage,
            meta: {
                appID,
                jobID
            },
            payload: data
        })

        return data
    }

export const startJobIdsOfBatchPoll =
    (appUUID: string, batchID: string) =>
    async (dispatch: MainThunkDispatch) => {
        return new Promise<JobIdsOfBatchResponse>(res => {
            dispatch(
                pollAPI(
                    JOB_IDS_OF_BATCH_POLL,
                    apiEndpoints.getJobIdsOfBatch({ appUUID, batchID }),
                    handleResult =>
                        handleResult !== undefined
                            ? res(handleResult)
                            : undefined,
                    undefined,
                    5000,
                    true
                )
            )
        })
    }

export const stopJobIdsOfBatchPoll = () => killPoll(JOB_IDS_OF_BATCH_POLL)

export const startJobIdsPoll =
    (jobUUID: string) => async (dispatch: MainThunkDispatch) => {
        return new Promise<APIJobModel>(res => {
            dispatch(
                pollAPI(
                    RELABEL_JOB_ID_POLL,
                    apiEndpoints.getJobByID({
                        jobID: jobUUID
                    }),
                    handleResult =>
                        handleResult !== undefined
                            ? res(handleResult)
                            : undefined,
                    undefined,
                    5000,
                    true
                )
            )
        })
    }

export const stopJobIdsPoll = () => killPoll(RELABEL_JOB_ID_POLL)

const setOneJobWithMessage =
    (jobID: string, appID: string, updatedJob?: APIJobModel) =>
    (dispatch: MainThunkDispatch, getState: () => State) => {
        const jobWithMessage = getValueById(
            getState().api.jobs.messageTable[appID],
            jobID
        )
        if (jobWithMessage !== undefined) {
            dispatch({
                type: JobAPIType.setOneJobWithMessage,
                meta: {
                    appID,
                    jobID
                },
                payload: updatedJob
                    ? {
                          ...jobWithMessage,
                          ...updatedJob
                      }
                    : undefined
            })
        }
    }

export const invalidateJobCounts = (appID: string): MainAction => ({
    type: JobAPIType.invalidateJobCount,
    meta: { appID }
})

export const pollForFirstInProgressJob =
    (pollId: string, appID: string, onResponse: (response: any) => void) =>
    (dispatch: MainThunkDispatch) => {
        dispatch(
            pollAPI(
                pollId,
                apiEndpoints.getJobList({
                    appID: appID,
                    page: 0,
                    pageSize: 1,
                    sortBy: JobSortBy.created,
                    orderBy: JobOrderBy.asc,
                    statusIn: [JobStatus.IN_PROGRESS]
                }),
                onResponse,
                undefined,
                3000
            )
        )
    }
